/**
 * Super-admin Payment Settings tab.
 *
 * The single place where all Razorpay billing config lives:
 *   1. Mode toggle (Test / Live) with clear active-mode indicator.
 *   2. Live and Test credentials — each with its own Key ID + Key Secret.
 *      Secrets use a show/hide toggle and are write-only from the UI
 *      (backend never returns them, just a masked …tail hint).
 *   3. Webhook configuration — the shared HMAC secret + a read-only
 *      Webhook URL derived from the current window origin.
 *   4. Plan IDs table for Starter / Professional / Growth / the
 *      Enterprise Workforce add-on, each with Monthly + Annual columns.
 *      Pre-populated with the platform defaults; editable & saveable.
 *
 * The Razorpay Plan IDs configured here are used consistently across the
 * app wherever ``client.subscription.create()`` is invoked — the main
 * SaaS subscription flow ``POST /api/subscriptions/create`` and the
 * Enterprise Workforce add-on checkout.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Key, CheckCircle, Warning, Eye, EyeSlash, Link as LinkIcon, Copy, Flask } from "@phosphor-icons/react";

// Table rows for the Plan IDs section. Order & labels here drive the
// UI; keep the ``key`` values in sync with the backend
// ``PLAN_TIER_KEYS`` list in ``platform_settings.py``.
const PLAN_ROWS = [
  { key: "starter",              label: "Starter" },
  { key: "professional",         label: "Professional" },
  { key: "growth",               label: "Growth" },
  {
    key: "enterprise_workforce",
    label: "TherapyStack Pro – Enterprise Workforce Management and Payroll Suite (Add-on)",
  },
];

const EMPTY_PLANS = () => ({
  starter:              { monthly: "", annual: "" },
  professional:         { monthly: "", annual: "" },
  growth:               { monthly: "", annual: "" },
  enterprise_workforce: { monthly: "", annual: "" },
});


export default function PaymentSettingsTab() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({
    mode: "test",
    live_key_id: "",
    live_key_secret: "",
    test_key_id: "",
    test_key_secret: "",
    webhook_secret: "",
    plan_ids: EMPTY_PLANS(),
  });
  const [show, setShow] = useState({
    live_secret: false, test_secret: false, webhook: false,
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/platform/razorpay-config");
      setCfg(data);
      setForm({
        mode: data.mode || "test",
        live_key_id: data.live_key_id || "",
        live_key_secret: "",
        test_key_id: data.test_key_id || "",
        test_key_secret: "",
        webhook_secret: "",
        plan_ids: { ...EMPTY_PLANS(), ...(data.plan_ids || {}) },
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Only send filled Plan IDs so blanks don't wipe the stored value.
      const cleaned_plans = {};
      for (const row of PLAN_ROWS) {
        const src = form.plan_ids[row.key] || {};
        const bucket = {};
        for (const c of ["monthly", "annual"]) {
          if (src[c] && src[c].trim()) bucket[c] = src[c].trim();
        }
        if (Object.keys(bucket).length) cleaned_plans[row.key] = bucket;
      }
      const payload = {
        mode: form.mode,
        live_key_id: form.live_key_id.trim(),
        live_key_secret: form.live_key_secret,   // empty = keep
        test_key_id: form.test_key_id.trim(),
        test_key_secret: form.test_key_secret,   // empty = keep
        webhook_secret: form.webhook_secret,     // empty = keep
        plan_ids: cleaned_plans,
      };
      const { data } = await api.put("/platform/razorpay-config", payload);
      setCfg(data);
      // Wipe secret inputs after save so re-typing is intentional.
      setForm(f => ({
        ...f,
        live_key_secret: "", test_key_secret: "", webhook_secret: "",
      }));
      toast.success("Payment settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!cfg) return null;

  const isLive = form.mode === "live";
  const modeBadge = isLive
    ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 gap-1" data-testid="mode-badge-live"><CheckCircle size={11} weight="fill" /> Live Mode Active</Badge>
    : <Badge className="bg-amber-100 text-amber-800 border-amber-200 gap-1" data-testid="mode-badge-test"><Flask size={11} weight="fill" /> Test Mode Active</Badge>;

  const webhookUrl = typeof window !== "undefined"
    ? `${window.location.origin}/api/webhook/razorpay-subscription`
    : "";

  const copyWebhook = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      toast.success("Webhook URL copied");
    } catch {
      toast.error("Copy failed — highlight and copy manually.");
    }
  };

  return (
    <div className="space-y-6" data-testid="payment-settings-tab">
      {/* ============ Razorpay API Credentials ============ */}
      <Card data-testid="rzp-credentials-card">
        <CardHeader>
          <CardTitle className="text-base font-display tracking-tight flex items-center gap-2">
            <Key size={16} weight="duotone" /> Razorpay API Credentials
            {modeBadge}
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            These credentials belong to the <span className="font-medium">platform</span> (Emergent), not to any tenant.
            They are used to bill workspaces for their SaaS subscription and Enterprise Workforce add-on.
            Switch between <span className="font-medium">Test Mode</span> (no real charges) and{" "}
            <span className="font-medium">Live Mode</span> (real billing) using the toggle below.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Mode toggle */}
          <div
            className="flex items-center justify-between rounded-md border border-border bg-muted/30 p-3"
            data-testid="rzp-mode-row"
          >
            <div>
              <div className="text-sm font-medium">Mode</div>
              <div className="text-[11px] text-muted-foreground">
                {isLive
                  ? "Live Mode — real charges. Use with a verified Razorpay live account."
                  : "Test Mode — sandbox only. No real charges will be made."}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={!isLive ? "text-sm font-medium" : "text-sm text-muted-foreground"}>Test</span>
              <Switch
                checked={isLive}
                onCheckedChange={(v) => setForm(f => ({ ...f, mode: v ? "live" : "test" }))}
                data-testid="rzp-mode-toggle"
              />
              <span className={isLive ? "text-sm font-medium" : "text-sm text-muted-foreground"}>Live</span>
            </div>
          </div>

          {/* Live credentials */}
          <div className={`rounded-md border p-4 space-y-3 ${isLive ? "border-emerald-300 bg-emerald-50/40" : "border-border"}`}>
            <div className="flex items-center gap-2">
              <div className="text-sm font-medium">Live Mode Credentials</div>
              {isLive && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">In use</Badge>}
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Key ID</Label>
                <Input
                  value={form.live_key_id}
                  onChange={(e) => setForm(f => ({ ...f, live_key_id: e.target.value }))}
                  placeholder="rzp_live_XXXXXXXXXX"
                  data-testid="rzp-live-key-id"
                />
              </div>
              <div className="space-y-1.5">
                <Label>
                  Key Secret{" "}
                  {cfg.live_key_secret_set && (
                    <span className="text-[10px] text-emerald-600">· stored (…{cfg.live_key_secret_hint})</span>
                  )}
                </Label>
                <SecretInput
                  value={form.live_key_secret}
                  onChange={(v) => setForm(f => ({ ...f, live_key_secret: v }))}
                  visible={show.live_secret}
                  onToggle={() => setShow(s => ({ ...s, live_secret: !s.live_secret }))}
                  placeholder={cfg.live_key_secret_set ? "Leave blank to keep current" : "Paste live key secret"}
                  testId="rzp-live-key-secret"
                />
              </div>
            </div>
          </div>

          {/* Test credentials */}
          <div className={`rounded-md border p-4 space-y-3 ${!isLive ? "border-amber-300 bg-amber-50/40" : "border-border"}`}>
            <div className="flex items-center gap-2">
              <div className="text-sm font-medium">Test Mode Credentials</div>
              {!isLive && <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">In use</Badge>}
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Test Key ID</Label>
                <Input
                  value={form.test_key_id}
                  onChange={(e) => setForm(f => ({ ...f, test_key_id: e.target.value }))}
                  placeholder="rzp_test_XXXXXXXXXX"
                  data-testid="rzp-test-key-id"
                />
              </div>
              <div className="space-y-1.5">
                <Label>
                  Test Key Secret{" "}
                  {cfg.test_key_secret_set && (
                    <span className="text-[10px] text-emerald-600">· stored (…{cfg.test_key_secret_hint})</span>
                  )}
                </Label>
                <SecretInput
                  value={form.test_key_secret}
                  onChange={(v) => setForm(f => ({ ...f, test_key_secret: v }))}
                  visible={show.test_secret}
                  onToggle={() => setShow(s => ({ ...s, test_secret: !s.test_secret }))}
                  placeholder={cfg.test_key_secret_set ? "Leave blank to keep current" : "Paste test key secret"}
                  testId="rzp-test-key-secret"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ============ Webhook Configuration ============ */}
      <Card data-testid="rzp-webhook-card">
        <CardHeader>
          <CardTitle className="text-base font-display tracking-tight flex items-center gap-2">
            <LinkIcon size={16} weight="duotone" /> Webhook Configuration
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Register the URL below on Razorpay dashboard → Settings → Webhooks, then paste the
            webhook secret shown there here so we can verify incoming events.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label>Webhook URL <span className="text-[10px] text-muted-foreground">(read only — register in Razorpay dashboard)</span></Label>
            <div className="flex gap-2">
              <Input readOnly value={webhookUrl} data-testid="rzp-webhook-url" className="font-mono text-xs" />
              <Button variant="outline" onClick={copyWebhook} className="gap-1" data-testid="rzp-webhook-copy">
                <Copy size={13} /> Copy
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Events to subscribe: <code>subscription.activated</code>, <code>subscription.charged</code>,{" "}
              <code>subscription.pending</code>, <code>subscription.halted</code>,{" "}
              <code>subscription.cancelled</code>, <code>subscription.completed</code>.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>
              Webhook Secret{" "}
              {cfg.webhook_secret_set && (
                <span className="text-[10px] text-emerald-600">· stored (…{cfg.webhook_secret_hint})</span>
              )}
            </Label>
            <SecretInput
              value={form.webhook_secret}
              onChange={(v) => setForm(f => ({ ...f, webhook_secret: v }))}
              visible={show.webhook}
              onToggle={() => setShow(s => ({ ...s, webhook: !s.webhook }))}
              placeholder={cfg.webhook_secret_set ? "Leave blank to keep current" : "Paste webhook secret from Razorpay dashboard"}
              testId="rzp-webhook-secret"
            />
          </div>
        </CardContent>
      </Card>

      {/* ============ Subscription Plan IDs ============ */}
      <Card data-testid="rzp-plans-card">
        <CardHeader>
          <CardTitle className="text-base font-display tracking-tight flex items-center gap-2">
            <Warning size={16} weight="duotone" /> Subscription Plan IDs
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            These Plan IDs must exist under Razorpay dashboard → Subscriptions → Plans, on the
            same account whose credentials are configured above. Each row supports a Monthly
            and an Annual Plan ID. All IDs look like <code>plan_XXXXXXXXXX</code>.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[42%]">Plan</TableHead>
                  <TableHead>Monthly Plan ID</TableHead>
                  <TableHead>Annual Plan ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {PLAN_ROWS.map(row => (
                  <TableRow key={row.key} data-testid={`rzp-plan-row-${row.key}`}>
                    <TableCell className="text-sm font-medium">{row.label}</TableCell>
                    <TableCell>
                      <Input
                        value={form.plan_ids[row.key]?.monthly || ""}
                        onChange={(e) => setForm(f => ({
                          ...f,
                          plan_ids: {
                            ...f.plan_ids,
                            [row.key]: { ...f.plan_ids[row.key], monthly: e.target.value },
                          },
                        }))}
                        placeholder="plan_XXXXXXXXXX"
                        className="font-mono text-xs"
                        data-testid={`rzp-plan-${row.key}-monthly`}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={form.plan_ids[row.key]?.annual || ""}
                        onChange={(e) => setForm(f => ({
                          ...f,
                          plan_ids: {
                            ...f.plan_ids,
                            [row.key]: { ...f.plan_ids[row.key], annual: e.target.value },
                          },
                        }))}
                        placeholder="plan_XXXXXXXXXX"
                        className="font-mono text-xs"
                        data-testid={`rzp-plan-${row.key}-annual`}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end sticky bottom-4">
        <Button
          onClick={save}
          disabled={saving}
          size="lg"
          className="shadow-lg"
          data-testid="save-payment-settings"
        >
          {saving ? "Saving…" : "Save Payment Settings"}
          {!saving && <CheckCircle size={15} className="ml-1.5" weight="duotone" />}
        </Button>
      </div>
    </div>
  );
}


function SecretInput({ value, onChange, visible, onToggle, placeholder, testId }) {
  return (
    <div className="relative">
      <Input
        type={visible ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="pr-10"
        data-testid={testId}
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
        aria-label={visible ? "Hide secret" : "Show secret"}
        data-testid={`${testId}-toggle`}
      >
        {visible ? <EyeSlash size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}
